import React, { Profiler, useContext, useEffect, useMemo, useState } from "react";
import { matchPath, Route, useHistory, useLocation, useParams } from "react-router-dom";
import { SearchboxController, SearchBoxHorizontalDefault } from "@bookingcom/flights-searchbox";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { HiddenVisually, Stack, Text, useTheme } from "@bookingcom/bui-react";
import { FlightDetailsDesktop, FlightDetailsNewDesktop, HomeDesktop } from "../app/routes.js";
import { AppShellDesktop } from "../components/elements/AppShell/index.js";
import Container from "../components/elements/Container/index.js";
import SearchLoadingScreen from "../components/elements/LoadingScreen/components/SearchLoadingScreen.js";
import SearchResultsDesktop from "../components/elements/SearchResults/SearchResults.desktop.js";
import { useClientFetch } from "../hooks/useClientFetch.js";
import useClientMetrics from "../hooks/useClientMetrics.js";
import { useSearchAPIParams } from "../hooks/useSearchAPIParams.js";
import useSearchbox from "../hooks/useSearchbox.js";
import { getSearchCriteria } from "../store/getSearchCriteria.js";
import { actions as LoaderActions } from "../store/loadingScreen/actions.js";
import { actions as SearchCriteriaActions } from "../store/searchCriteria/actions.js";
import { actions as searchResultsActions } from "../store/searchResults/actions.js";
import { actions as flexibleDateMinPriceActions } from "../store/flexibleDateMinPrice/actions.js";
import { useActions, useStore } from "../store/index.js";
import { PageHeader } from "./Search.js";
import useATOL from "../hooks/useATOL.js";
import { useFlightSearchResultsGTMTag } from "../hooks/useGTMFlightTags.js";
import { useAdTechSearchResultsTag } from "../hooks/useAdTechGTMTags.js";
import useLocaleContext from "../hooks/useLocaleContext.js";
import useReloadIfSearchStoreMissing, { isSearchStoreInitial } from "../hooks/useReloadIfSearchStoreMissing.js";
import { isOfMetaOrigin } from "../utils/marketing-url-params.js";
import { getMarketingTrackingVariable, isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
import useRouteName from "../hooks/useRouteName.js";
import useTrackPageLoad from "../hooks/useTrackPageLoad.js";
import useLandingPageOfferPriceAccuracyEvent from "../hooks/c360/useLandingPageOfferPriceAccuracyEvent.js";
import { flightsSessionStore } from "@flights/web-api-utils-universal";
import StaticContext from "../app/StaticContext/StaticContext.js";
import useRemoveExternalParamsFromUrl from "../hooks/useRemoveExternalParamsFromUrl.js";
import useMeasureSearchToFlightDetails from "../hooks/useMeasureSearchToFlightDetails.js";
import { trackMetaChannelStagesV2 } from "../utils/experiments/track-meta-landings-v2.js";
import Frame from "../components/elements/Frame/index.js";
import { useFlexDatesRectrictedPOS } from "../components/elements/FlexibleDateBestPrice/useFlexDatesRectrictedPOS.js";
import { useDispatch, useSelector } from "react-redux";
import { actions as brandedFaresActions } from "../store/brandedFareOffers/actions.js";
import { goal, trackWithDefaultStage } from "@flights/et-universal";
import useFareCalendarEligible from "../hooks/useFareCalendarEligible.js";
import DSABannerDesktop from "../components/elements/DSABanner/DSABanner.desktop.js";
import useFlightsAaWebFunnelSearchresutlTrafficFe from "../utils/experiments/funnel/flights_web_funnel_aa_fe_sr.js";
import { useSpringDeal } from "../hooks/useSpringDeal.js";
import { useSummerDeal } from "../hooks/useSummerDeal.js";
import useGlobalContext from "../hooks/useGlobalContext.js";
import useProfiler from "../hooks/useProfiler.js";
import { WishlistWidgetLoader, OptInAANeetiTracker } from "../components/remote/index.js";
import { useDelayResponse, useGetStaggeredSearchResults } from "../utils/experiments/funnel/flights_funnel_fake_staggered_search_web.js";
import LoadingProgressBar from "../components/elements/SearchResults/LoadingProgressBar.js";
import { getSearchResultsVisibleIndexes } from "../store/searchResults/selectors.js";
import { useFlightsWeb2StepMlRanker } from "../utils/experiments/funnel/flights_web_2_step_ml_ranker.js";
const SearchDesktop = ({ getInitialState }) => {
    const staticContext = useContext(StaticContext);
    const params = useParams();
    const store = useStore();
    const location = useLocation();
    const actions = useActions(searchResultsActions);
    const flexibleActions = useActions(flexibleDateMinPriceActions);
    const reactRouterHistory = useHistory();
    const loaderActions = useActions(LoaderActions);
    const dispatch = useDispatch();
    const isMetaOrigin = getMarketingTrackingVariable("ext-origin");
    const searchCriteriaActions = useActions(SearchCriteriaActions);
    const theme = useTheme();
    const trackSRContextEventV2 = useTrackPageLoad("search_results");
    const trackSRSkeletonContextEventV2 = useTrackPageLoad("search_results_skeleton");
    const trackSRModalContextEventV2 = useTrackPageLoad("search_results_modal");
    const trackLandingPageOfferPriceAccuracyEvent = useLandingPageOfferPriceAccuracyEvent();
    const searchBoxProps = useSearchbox();
    const i18n = useI18n();
    const queryParams = new URLSearchParams(location.search);
    const { isRTL } = useLocaleContext();
    const [preloadDetails, toggle] = useState(false);
    const [previousPath, setPreviousPath] = useState("");
    const { trackRenderTime } = useClientMetrics();
    const fireByeahSearchResultsTag = useFlightSearchResultsGTMTag();
    const fireAdTechSearchResultsTag = useAdTechSearchResultsTag();
    const { isATolProtectedSR } = useATOL();
    const isRestricedFlexDatePOS = useFlexDatesRectrictedPOS();
    const datepickerOpen = !!Number(queryParams.get("open_datepicker"));
    const { currencyCode, features } = useGlobalContext();
    const isFareCalendarEligible = useFareCalendarEligible();
    if (getInitialState && staticContext.set) {
        staticContext.set({ getInitialState: getInitialState(queryParams) });
    }
    const [lastTrackedPageLoad, setLastTrackedPageLoad] = useState("");
    const { visible: loadingScreenVisible } = store.loadingScreen;
    const { onFetchedResults, onSelectFlight } = useMeasureSearchToFlightDetails();
    const { isSpringDealExp } = useSpringDeal();
    const { isSummerDealExp } = useSummerDeal();
    const { trackStages } = useFlightsAaWebFunnelSearchresutlTrafficFe();
    useEffect(() => {
        trackStages();
    }, [trackStages]);
    useRemoveExternalParamsFromUrl();
    useEffect(() => {
        trackMetaChannelStagesV2("desktop");
    }, []);
    useEffect(() => {
        goal("flights_search_results_page_view");
        goal("tbu_vertical_search_results_view");
    }, []);
    const routeName = useRouteName();
    const searchAPIParams = useSearchAPIParams();
    const url = `/api/flights/?${searchAPIParams}`;
    const urlFlexibleDatePrice = useMemo(() => {
        const params = new URLSearchParams(searchAPIParams);
        params.set("useAggregateCache", "1");
        params.set("compareBestPrice", "1");
        return `/api/search/min-price/?${params.toString()}`;
    }, [searchAPIParams]);
    useEffect(() => {
        return reactRouterHistory.listen((location) => {
            if (reactRouterHistory.action === "POP") {
                if (routeName === "searchresults" &&
                    matchPath(location.pathname, { exact: true, path: "/flights/:airports" }) &&
                    reactRouterHistory.location.hash !== "#basiclayout") {
                    window.location.reload();
                }
            }
            setPreviousPath(location.pathname);
        });
    }, [previousPath]);
    const delayFetchBy = !!isMetaOrigin && routeName === "flightdetails" ? 500 : 0;
    const [isLandedOnFD, setIsLandedOnFD] = useState(undefined);
    useEffect(() => {
        if (isLandedOnFD !== undefined)
            return;
        setIsLandedOnFD(routeName === "flightdetails");
    }, [isLandedOnFD, routeName]);
    const delayResponse = useDelayResponse();
    const getStaggeredSearchResults = useGetStaggeredSearchResults();
    useFlightsWeb2StepMlRanker();
    useClientFetch(url, {
        delayFetchBy,
        condition: () => {
            if (isSearchStoreInitial(store.search))
                return false;
            return true;
        },
        fetch: () => {
            if (!location?.state?.hideLoader && routeName !== "flightdetails") {
                history.replaceState({ hideLoader: false }, document.title);
                loaderActions.show();
                setTimeout(() => {
                    loaderActions.hide();
                }, 6000);
            }
            actions.fetch();
        },
        success: (data) => {
            const { variant, latency, visibleIndexes } = getStaggeredSearchResults(searchAPIParams, data.source === "cache");
            const processSuccess = () => {
                searchCriteriaActions.setSearchCriteria(getSearchCriteria(queryParams, store.search));
                if (data.flightOffers.length > 0) {
                    data.flightOffers[0].highlightInfo?.highlight &&
                        flightsSessionStore.set("highlightedOfferToken", data.flightOffers[0].token);
                    data.flightOffers.forEach((offer) => {
                        if (offer && !offer.requestableBrandedFares && offer.brandedFareOffers) {
                            dispatch(brandedFaresActions.fetchSuccess(offer.brandedFareOffers, offer.token, offer.brandedFareComparisonTable, offer.brandedFareComparisonTableCompact));
                        }
                    });
                }
                if (latency && variant === 2) {
                    actions.hidePrice();
                    delayResponse(() => {
                        actions.showPrice();
                    }, latency);
                }
                if (latency && variant === 3 && visibleIndexes) {
                    actions.addVisibleIndexes(visibleIndexes);
                    delayResponse(() => {
                        actions.clearVisibleIndexes();
                    }, latency);
                }
                actions.fetchSuccess(data);
                trackLandingPageOfferPriceAccuracyEvent(data, location.search);
                fireByeahSearchResultsTag();
                void fireAdTechSearchResultsTag();
                loaderActions.hide();
                onFetchedResults();
            };
            if (latency && variant === 1) {
                delayResponse(() => {
                    processSuccess();
                }, latency);
            }
            else {
                processSuccess();
            }
        },
        error: (data) => {
            goal("flights_search_results_loading_error");
            loaderActions.hide();
            actions.fetchError(data);
        },
        forceRefetch: location?.state?.forceRefetch
    });
    useClientFetch(urlFlexibleDatePrice, {
        delayFetchBy,
        fetch: () => flexibleActions.fetch(),
        success: (data) => flexibleActions.fetchSuccess(data),
        error: (data) => flexibleActions.fetchError(data),
        condition: () => {
            if (store.flexibleDateMinPrice.fetchStatus !== "success") {
                return store.search.cabinClass === "ECONOMY" && !isRestricedFlexDatePOS && !isOfMetaOrigin();
            }
            return false;
        },
        forceRefetch: location?.state?.forceRefetch
    });
    const { fetchStatus } = store.searchResults;
    const { searchSegments } = store.search;
    const searchBoxControllerProps = {
        ...searchBoxProps,
        i18n,
        defaultSegmentLength: searchSegments.length,
        isDateDropdownOpen: datepickerOpen
    };
    if (!preloadDetails && fetchStatus === "success") {
        toggle(true);
        FlightDetailsDesktop.preload();
        HomeDesktop.preload();
    }
    useEffect(() => {
        if (["initial", "loading"].includes(fetchStatus)) {
            if (loadingScreenVisible && lastTrackedPageLoad !== "search_results_modal") {
                trackSRModalContextEventV2();
                setLastTrackedPageLoad("search_results_modal");
            }
            else if (lastTrackedPageLoad !== "search_results_skeleton") {
                trackSRSkeletonContextEventV2();
                setLastTrackedPageLoad("search_results_skeleton");
            }
        }
        else if (lastTrackedPageLoad !== "search_results") {
            trackSRContextEventV2();
            setLastTrackedPageLoad("search_results");
        }
    }, [fetchStatus]);
    const { isLoggedInAuthLow } = useProfiler();
    const searchResultsVisibleIndexes = useSelector(getSearchResultsVisibleIndexes);
    if (useReloadIfSearchStoreMissing(store.search)) {
        return null;
    }
    isATolProtectedSR(store?.searchResults?.atolProtectedStatus);
    return (React.createElement(Profiler, { id: "screen", onRender: trackRenderTime },
        React.createElement(PageHeader, { segments: searchSegments }),
        React.createElement(AppShellDesktop, null,
            React.createElement(Frame, { direction: "column", grow: 1, justifyContent: "center", attributes: { style: { width: "100%" } } },
                React.createElement("div", { style: { background: theme.colors.color_background_base_alt } },
                    React.createElement(Container, { pt: 6, pb: 6 },
                        React.createElement(HiddenVisually, null,
                            React.createElement(Text, { tagName: "h1" }, i18n.trans(t("a11y_flights_sr_search_hidden_heading")))),
                        React.createElement(Stack, { gap: 2 },
                            React.createElement(SearchboxController, { ...searchBoxControllerProps, isRTL: isRTL, isResponsiveFixes: true, enabledCountriesAndAnywhere: true, isSpringDealExp: isSpringDealExp, isSummerDealExp: isSummerDealExp, isSearchAnyCityEnabled: true, fareCalendarConfig: {
                                    isFareCalendarDesktopEnabled: isFareCalendarEligible &&
                                        (!!trackWithDefaultStage("flights_web_cat_fare_calendar_oneway", 1) ||
                                            !!trackWithDefaultStage("flights_web_cat_fare_calendar_data_collection", 1)),
                                    isFareCalendarDisplayEnabled: isFareCalendarEligible && !!trackWithDefaultStage("flights_web_cat_fare_calendar_oneway", 1),
                                    currencyCode
                                }, isCalendarReflowFixEnabled: isFeatureRunningClientSide("FLIGHTS_WEB_CAT_CALENDAR_FIX"), showCabinClassLabel: !!trackWithDefaultStage("flights_cat_cabin_class_label", 1) }, () => React.createElement(SearchBoxHorizontalDefault, null)),
                            React.createElement(DSABannerDesktop, null)))),
                React.createElement("div", { style: { position: "relative" } }, !!searchResultsVisibleIndexes && React.createElement(LoadingProgressBar, null)),
                React.createElement(Container, { pt: 6 },
                    React.createElement(Frame, { direction: "column" },
                        React.createElement(SearchResultsDesktop, { airports: params?.airports, onSelectFlight: onSelectFlight }))),
                React.createElement(Route, { key: "/flights/:airports/:id", path: "/flights/:airports/:id", exact: true }, !!trackWithDefaultStage("flights_funnel_new_fd_page_desktop", 1) ? (React.createElement(FlightDetailsNewDesktop, null)) : (React.createElement(FlightDetailsDesktop, null))),
                routeName === "searchresults" && !isLandedOnFD && React.createElement(SearchLoadingScreen, null),
                features["FLIGHTS_WEB_WISHLIST_WIDGET_DESKTOP_SSR"] && isLoggedInAuthLow() ? (React.createElement(WishlistWidgetLoader, null)) : undefined,
                features["FLIGHTS_WEB_SR_OPT_IN_AA_NEETI_TRACKER"] ? React.createElement(OptInAANeetiTracker, null) : undefined))));
};
export default React.memo(SearchDesktop);
